<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-0 p-[15px] md:p-[20px] rounded-md flex flex-col h-full"
  >
    <div class="trezo-card-header sm:flex items-center justify-between">
      <div class="trezo-card-title">
        <div class="flex items-center">
          <button 
            v-if="isMobile"
            @click="backToList"
            class="inline-block transition-all hover:text-primary-500 ltr:mr-[10px] rtl:ml-[10px]"
            :aria-label="directT('chat.backToList')"
          >
            <i class="material-symbols-outlined !text-xl"> arrow_back </i>
          </button>
          <div class="relative ltr:mr-[15px] rtl:ml-[15px]">
            <img
              :src="chatUser.profile_picture_url || chatUser.image"
              alt="user-image"
              class="rounded-full w-[75px]"
            />
            <span
              class="absolute bg-success-500 w-[10px] h-[10px] rounded-full border-[2px] border-white dark:border-[#0c1427] bottom-[5px] ltr:right-[5px] rtl:left-[5px]"
            ></span>
          </div>
          <div>
            <span
              class="font-semibold block text-black dark:text-white text-md"
            >
              {{ chatUser.name || chatUser.phone }}
            </span>
            <span class="mt-[2px]" v-if="chatUser.phone && chatUser.name"> {{ chatUser.phone }} </span>
          </div>
        </div>
      </div>
      <div
        class="trezo-card-subtitle mt-[15px] sm:mt-0 flex items-center gap-[15px]"
      >
        <!-- כפתור להקצאת שיחה -->
        <div class="relative inline-flex items-center">
          <button
            type="button"
            @click="toggleAssignDropdown"
            class="inline-flex items-center justify-center h-8 w-8 transition-all hover:text-primary-500 relative"
            :title="assignedToName ? `Assigned to ${assignedToName}` : 'Assign to user'"
          >
            <i class="material-symbols-outlined !text-md"> person_add </i>
            <div 
              v-if="assignedToName" 
              class="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full"
              :title="assignedToName"
            ></div>
          </button>
          <!-- תפריט משתמשים -->
          <div 
            v-if="showAssignDropdown" 
            class="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-[#0c1427] shadow-lg rounded-md z-50 border border-gray-200 dark:border-[#172036]"
          >
            <div v-if="assignedToName" class="px-3 py-2 border-b border-gray-200 dark:border-[#172036] text-xs">
              {{ directT('chat.assignedTo') || 'Assigned to' }}: <span class="font-semibold">{{ assignedToName }}</span>
            </div>
            <div class="max-h-40 overflow-y-auto py-1">
              <div 
                v-for="user in orgUsers" 
                :key="user.user_id" 
                @click="assignToUser(user.user_id)"
                class="px-3 py-2 hover:bg-gray-100 dark:hover:bg-[#172036] cursor-pointer"
              >
                {{ user.display_name }}
              </div>
              <div 
                @click="unassignUser"
                class="px-3 py-2 hover:bg-gray-100 dark:hover:bg-[#172036] cursor-pointer text-danger-500"
              >
                {{ directT('chat.unassign') || 'Unassign' }}
              </div>
            </div>
          </div>
        </div>
        <button
          type="button"
          :class="{
            'inline-flex items-center justify-center h-8 w-8 transition-all hover:text-primary-500 rounded-full p-1': true,
            'bg-success-500 shadow-md': chatUser.botstatus === true,
            'bg-danger-500 shadow-md': chatUser.botstatus === false
          }"
          :title="chatUser.botstatus ? 'Bot is active' : 'Bot is inactive'"
          @click="toggleBotStatus"
        >
          <i class="material-symbols-outlined !text-md" :class="{'text-white font-bold': chatUser.botstatus !== undefined}"> smart_toy </i>
        </button>
      </div>
    </div>

    <div class="trezo-card-content flex flex-col flex-grow overflow-hidden">
      <div
        class="border-t border-gray-100 dark:border-[#172036] mt-[20px] mb-[18px]"
      ></div>
      <div
        class="chat-body calc-height overflow-y-auto ltr:-mr-[10px] rtl:-ml-[10px] flex flex-col-reverse"
      >
        <ul
          class="flex flex-col ltr:pl-[0px] ltr:pr-[10px] rtl:pr-[10px] rtl:pl-[10px] pb-[5px] w-full"
        >
          <li
            v-for="(message, index) in messages"
            :key="index"
            :class="{
              'relative w-full md:w-full ltr:text-right md:rtl:text-left md:ltr:mr-auto rtl:ml-auto': message.from_our_customer,
              'relative ltr:pl-[40px] rtl:pr-[40px] w-full md:w-full': !message.from_our_customer
            }"
          >
            <img
              v-if="!message.from_our_customer"
              :src="chatUser.profile_picture_url || chatUser.image"
              width="35"
              alt="user"
              class="rounded-full top-0 ltr:left-0 rtl:right-0 absolute"
            />
            <div :class="{'ltr:text-right rtl:text-left': message.from_our_customer, 'ltr:text-left rtl:text-right': !message.from_our_customer}">
              <div class="mb-[5px] last:mb-0">
                <!-- Text message -->
                <p
                  v-if="message.type === 'text'"
                  :class="{
                    'py-[10px] px-[15px] inline-block bg-primary-500 text-white ltr:rounded-l-md rtl:rounded-l-md max-w-[90%] break-words whitespace-pre-wrap': message.from_our_customer,
                    'py-[10px] px-[15px] inline-block bg-gray-50 dark:bg-[#15203c] ltr:rounded-r-md rtl:rounded-l-md max-w-[90%] break-words whitespace-pre-wrap': !message.from_our_customer
                  }"
                  v-html="formatMessageText(message.text)"
                >
                </p>
                
                <!-- Image message -->
                <div
                  v-else-if="message.type === 'image' || message.type === 'photo'"
                  :class="{
                    'py-[5px] px-[5px] inline-block bg-primary-500 text-white ltr:rounded-l-md rtl:rounded-l-md max-w-[90%]': message.from_our_customer,
                    'py-[5px] px-[5px] inline-block bg-gray-50 dark:bg-[#15203c] ltr:rounded-r-md rtl:rounded-l-md max-w-[90%]': !message.from_our_customer
                  }"
                >
                  <img 
                    :src="message.link" 
                    alt="Image" 
                    class="max-w-full rounded-md max-h-[300px] cursor-pointer"
                    @click="openImageInNewTab(message.link)"
                  />
                  <p v-if="message.text" class="mt-2 px-2 text-sm" v-html="formatMessageText(message.text)"></p>
                </div>
                
                <!-- Audio message -->
                <div
                  v-else-if="message.type === 'audio' || message.type === 'voice'"
                  :class="{
                    'py-[10px] px-[15px] inline-block bg-primary-500 text-white ltr:rounded-l-md rtl:rounded-l-md max-w-[90%]': message.from_our_customer,
                    'py-[10px] px-[15px] inline-block bg-gray-50 dark:bg-[#15203c] ltr:rounded-r-md rtl:rounded-l-md max-w-[90%]': !message.from_our_customer
                  }"
                >
                  <audio controls class="max-w-full">
                    <source :src="message.link" type="audio/mpeg">
                    Your browser does not support the audio element.
                  </audio>
                  <p v-if="message.text" class="mt-2" v-html="formatMessageText(message.text)"></p>
                </div>
                
                <!-- Default for other message types -->
                <p
                  v-else
                  :class="{
                    'py-[10px] px-[15px] inline-block bg-primary-500 text-white ltr:rounded-l-md rtl:rounded-l-md max-w-[90%] break-words whitespace-pre-wrap': message.from_our_customer,
                    'py-[10px] px-[15px] inline-block bg-gray-50 dark:bg-[#15203c] ltr:rounded-r-md rtl:rounded-l-md max-w-[90%] break-words whitespace-pre-wrap': !message.from_our_customer
                  }"
                  v-html="formatMessageText(message.text)"
                >
                </p>
              </div>
            </div>
            <span
              :class="{
                'block text-xs ltr:text-right rtl:text-left mb-[15px] mt-[5px]': message.from_our_customer,
                'block text-xs ltr:text-left rtl:text-right mb-[15px] mt-[5px]': !message.from_our_customer
              }"
            >
              {{ formatTime(message.created_at) }}
            </span>
          </li>
        </ul>
      </div>
      <div class="chat-form mt-[10px] relative">
        <form class="relative" @submit.prevent="sendMessage">
          <textarea
            :placeholder="directT('chat.writeMessage')"
            class="w-full h-[50px] resize-none rounded-md text-black dark:text-white bg-gray-50 dark:bg-[#15203c] border border-gray-50 dark:border-[#15203c] transition-all focus:border-primary-500 outline-0 text-xs placeholder:text-gray-500 pt-[16px] ltr:pl-[16px] rtl:pr-[16px] ltr:pr-[150px] rtl:pl-[150px] dark:placeholder:text-gray-400"
            v-model="messageText"
          ></textarea>
          <div class="absolute ltr:right-0 rtl:left-0 top-0 h-full w-[150px] flex items-center justify-end ltr:pr-[13px] rtl:pl-[13px] gap-[9px]">
            <div class="emoji-button w-[30px] h-[30px] flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-[#0a0e19] transition-all cursor-pointer relative" @click.stop="toggleEmojiPicker">
              <i class="material-symbols-outlined text-[18px]">sentiment_satisfied</i>
            </div>
            
            <!-- Emoji picker with categories --> 
            <div v-if="showEmojiPicker" class="emoji-picker fixed bottom-auto right-auto bg-white dark:bg-[#0c1427] p-2 rounded-md shadow-xl z-[100] w-[300px] h-[350px] overflow-hidden" style="transform: translateY(-360px)">
              <!-- Category tabs -->
              <div class="emoji-tabs flex border-b border-gray-200 dark:border-[#172036] mb-2">
                <div 
                  v-for="(category, index) in emojiCategories" 
                  :key="index"
                  @click="currentEmojiCategory = category.id"
                  class="cursor-pointer p-1 text-center flex-1 text-sm hover:bg-gray-100 dark:hover:bg-[#15203c]"
                  :class="{ 'bg-gray-100 dark:bg-[#15203c]': currentEmojiCategory === category.id }"
                >
                  {{ category.icon }}
                </div>
              </div>
              
              <!-- Category title -->
              <div class="text-xs font-medium mb-1 px-1">
                {{ emojiCategories.find(c => c.id === currentEmojiCategory)?.name || 'אימוג׳ים' }}
              </div>
              
              <!-- Emojis grid -->
              <div class="emoji-grid h-[295px] overflow-y-auto px-1">
                <div class="grid grid-cols-8 gap-1">
                  <div 
                    v-for="emoji in currentEmojis" 
                    :key="emoji"
                    @click.stop="addEmoji(emoji)" 
                    class="cursor-pointer text-lg hover:bg-gray-100 dark:hover:bg-[#15203c] p-1 rounded text-center"
                  >
                    {{ emoji }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="w-[30px] h-[30px] flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-600 text-white transition-all cursor-pointer" @click.stop="sendMessage">
              <i class="material-symbols-outlined text-[18px]" :title="directT('chat.send')">send</i>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";
import { supabase } from "@/utils/supabase";
import { useUserStore } from '@/store/user';
import axios from 'axios';

// Interface for message data
interface Message {
  id: number;
  text: string;
  phone: string;
  created_at: string;
  from_our_customer: boolean;
  organization_id: string;
  type: string;
  qtext: string | null;
  link: string | null;
}

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

export default defineComponent({
  name: "ChatPanel",
  props: {
    selectedChat: {
      type: Object,
      default: null,
    },
  },
  emits: ["back-to-list", "update-bot-status"],
  setup(props, { emit }) {
    // i18n setup
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };

    const isActive = ref(false);
    const isMobile = ref(false);
    const messageText = ref('');
    const showEmojiPicker = ref(false);
    const currentEmojiCategory = ref('smileys');
    
    // משתנים עבור הקצאת משתמש
    const showAssignDropdown = ref(false);
    const orgUsers = ref<any[]>([]);
    const assignedToName = ref("");
    const assignedToId = ref<string | null>(null);
    
    // בדיקה אם המכשיר הוא מובייל
    const checkIfMobile = (): void => {
      isMobile.value = window.innerWidth < 1024; // lg breakpoint in Tailwind
    };
    
    // רשימת אימוג'ים פשוטה
    const emojis = [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
      '😉', '😊', '😇', '😍', '🥰', '😘', '😗', '😚', '😙', '😋',
      '😛', '😜', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨',
      '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔',
      '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵',
      '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '👏', '🙏', '🤝',
      '💪', '❤️', '💔', '💯', '💢', '💥', '💫', '💦', '💨', '👀',
    ];
    
    // קטגוריות של אימוג'ים
    const emojiCategories = [
      { id: 'smileys', name: 'Emojis', icon: '😀' },
      { id: 'people', name: 'People', icon: '👪' },
      { id: 'animals', name: 'Animals', icon: '🐱' },
      { id: 'food', name: 'Food', icon: '🍔' },
      { id: 'travel', name: 'Travel', icon: '✈️' },
      { id: 'activities', name: 'Activities', icon: '⚽' },
      { id: 'objects', name: 'Staff', icon: '💡' },
      { id: 'symbols', name: 'Smileys', icon: '❤️' }
    ];
    
    // אימוג'ים מקובצים לפי קטגוריות
    const emojisByCategory = {
      smileys: [
        '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', 
        '🥰', '😍', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '😝', '🤑', '🤗', '🤭', 
        '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', 
        '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', 
        '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕', '😟', '🙁', '☹️', '😮', '😯', 
        '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', 
        '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', 
        '💩', '🤡', '👹', '👺', '👻', '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼', 
        '😽', '🙀', '😿', '😾'
      ],
      people: [
        '👶', '👧', '🧒', '👦', '👩', '🧑', '👨', '👵', '🧓', '👴', '👮', '🕵️', '💂', 
        '👷', '🤴', '👸', '👳', '👲', '🧕', '🧔', '👱', '👨‍🦰', '👨‍🦱', '👨‍🦳', '👨‍🦲', 
        '👩‍🦰', '👩‍🦱', '👩‍🦳', '👩‍🦲', '🤵', '👰', '🤰', '🤱', '👼', '🎅', '🤶', '🦸', 
        '🦹', '🧙', '🧚', '🧛', '🧜', '🧝', '🧞', '🧟', '💆', '💇', '🚶', '🧍', '🧎', 
        '🏃', '💃', '🕺', '🕴️', '👯', '🧖', '🧗', '🤺', '🏇', '⛷️', '🏂', '🏌️', '🏄', 
        '🚣', '🏊', '⛹️', '🏋️', '🚴', '🚵', '🤸', '🤼', '🤽', '🤾', '🤹', '🧘', '🛀', 
        '🛌', '👭', '👫', '👬', '💏', '💑', '👪', '🗣️', '👤', '👥', '👣', '👋', '🤚', '✋', 
        '🖖', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '👍', '👎', 
        '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🙏', '✍️', '💅', '🤳', '💪', 
        '🦵', '🦶', '👂', '👃', '🧠', '🦷', '🦴', '👀', '👁️', '👅', '👄'
      ],
      animals: [
        '🐵', '🐒', '🦍', '🦧', '🐶', '🐕', '🦮', '🐩', '🐺', '🦊', '🐱', '🐈', 
        '🦁', '🐯', '🐅', '🐆', '🐴', '🐎', '🦄', '🦓', '🦌', '🐮', '🐂', '🐃', '🐄', 
        '🐷', '🐖', '🐗', '🐽', '🐏', '🐑', '🐐', '🐪', '🐫', '🦙', '🦒', '🐘', '🦏', 
        '🦛', '🐭', '🐁', '🐀', '🐹', '🐰', '🐇', '🦔', '🐻', '🐨', '🐼', 
        '🦡', '🐾', '🦃', '🐔', '🐓', '🐣', '🐤', '🐥', '🐦', 
        '🐧', '🦅', '🦆', '🦢', '🦉', '🦚', '🦜', '🐸', '🐊', '🐢', '🦎', 
        '🐍', '🐲', '🐉', '🦕', '🦖', '🐳', '🐋', '🐬', '🐟', '🐠', '🐡', '🦈', '🐙', 
        '🐚', '🐌', '🦋', '🐛', '🐜', '🐝', '🐞', '🕷️', '🦂', '🦟'
      ],
      food: [
        '🍇', '🍈', '🍉', '🍊', '🍋', '🍌', '🍍', '🥭', '🍎', '🍏', '🍐', '🍑', '🍒', 
        '🍓', '🥝', '🍅', '🥥', '🥑', '🍆', '🥔', '🥕', '🌽', '🌶️', '🥒', '🥬', '🥦', 
        '🧄', '🧅', '🍄', '🥜', '🌰', '🍞', '🥐', '🥖', '🥨', '🥯', '🥞', '🧇', '🧀', 
        '🍖', '🍗', '🥩', '🥓', '🍔', '🍟', '🍕', '🌭', '🥪', '🌮', '🌯', '🥙', '🧆', 
        '🥚', '🍳', '🥘', '🍲', '🥣', '🥗', '🍿', '🧈', '🧂', '🥫', '🍱', '🍘', '🍙', 
        '🍚', '🍛', '🍜', '🍝', '🍠', '🍢', '🍣', '🍤', '🍥', '🥮', '🍡', '🥟', '🥠', 
        '🥡', '🦀', '🦞', '🦐', '🦑', '🦪', '🍦', '🍧', '🍨', '🍩', '🍪', '🎂', '🍰', 
        '🧁', '🥧', '🍫', '🍬', '🍭', '🍮', '🍯', '🍼', '🥛', '☕', '🍵', '🍶', '🍾', 
        '🍷', '🍸', '🍹', '🍺', '🍻', '🥂', '🥃', '🥤', '🧃', '🧉', '🧊', '🥢', '🍽️', 
        '🍴', '🥄'
      ],
      travel: [
        '🚂', '🚃', '🚄', '🚅', '🚆', '🚇', '🚈', '🚉', '🚊', '🚝', '🚞', '🚋', '🚌', 
        '🚍', '🚎', '🚐', '🚑', '🚒', '🚓', '🚔', '🚕', '🚖', '🚗', '🚘', '🚙', '🚚', 
        '🚛', '🚜', '🏎️', '🏍️', '🛵', '🦽', '🦼', '🛺', '🚲', '🛴', '🛹', '🚏', '🛣️', 
        '🛤️', '⛽', '🚨', '🚥', '🚦', '🛑', '🚧', '⚓', '⛵', '🛶', '🚤', '🛳️', '⛴️', 
        '🛥️', '🚢', '✈️', '🛩️', '🛫', '🛬', '🪂', '💺', '🚁', '🚟', '🚠', '🚡', '🛰️', 
        '🚀', '🛸', '🗿', '🏠', '🏡', '🏢', '🏣', '🏤', '🏥', '🏦', '🏨', '🏩', '🏪', 
        '🏫', '🏬', '🏭', '🏯', '🏰', '💒', '🗼', '🗽', '⛪', '🕌', '🛕', '🕍', '⛩️', 
        '🕋', '⛲', '⛺', '🏕️', '🏖️', '🏜️', '🏝️', '🏞️', '🏟️', '🏛️', '🏗️', '🪨', 
        '🪵', '🛖', '🏘️', '🏚️', '🏙️', '🗾', '🌋', '🏔️', '⛰️', '🗻', '🏞️', '🏤', '🏥', 
        '🏭', '🏯', '🏰'
      ],
      activities: [
        '⚽', '⚾', '🥎', '🏀', '🏐', '🏈', '🏉', '🎾', '🥏', '🎳', '🏏', '🏑', '🏒', 
        '🥍', '🏓', '🏸', '🥊', '🥋', '🥅', '⛳', '⛸️', '🎣', '🤿', '🎽', '🎿', '🛷', 
        '🥌', '🎯', '🪀', '🪁', '🎱', '🔮', '🧿', '🎮', '🕹️', '🎰', '🎲', '🧩', '🧸', 
        '♠️', '♥️', '♦️', '♣️', '♟️', '🃏', '🀄', '🎴', '🎭', '🖼️', '🎨', '🧵', '🧶', 
        '👓', '🕶️', '🥽', '🥼', '🦺', '👔', '👕', '👖', '🧣', '🧤', '🧥', '🧦', '👗', 
        '👘', '🥻', '🩱', '🩲', '🩳', '👙', '👚', '👛', '👜', '👝', '🛍️', '🎒', '👞', 
        '👟', '🥾', '🥿', '👠', '👡', '🩰', '👢', '👑', '👒', '🎩', '🎓', '🧢', '⛑️', 
        '📿', '💄', '💍', '💎', '🔇', '🔈', '🔉', '🔊', '📢', '📣', '📯', '🔔', '🎼', 
        '🎵', '🎶', '🎙️', '🎚️', '🎛️', '🎤', '🎧', '📻', '🎷', '🎸', '🎹', '🎺', '🎻', 
        '🪕', '🥁'
      ],
      objects: [
        '⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '🕹️', '🗜️', '💽', '💾', 
        '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽️', '🎞️', '📞', '☎️', '📟', '📠', 
        '📺', '📻', '🎙️', '🎚️', '🎛️', '🧭', '⏱️', '⏲️', '⏰', '🕰️', '⌛', '⏳', '📡', 
        '🔋', '🔌', '💡', '🔦', '🕯️', '🧯', '🛢️', '💸', '💵', '💴', '💶', '💷', '💰', 
        '💳', '💎', '⚖️', '🧰', '🔧', '🔨', '⚒️', '🛠️', '⚒️', '🔩', '⚙️', '🔧', '⛓️', 
        '🧲', '🔫', '💣', '🧨', '🪓', '🔪', '🗡️', '⚔️', '🛡️', '🚬', '⚰️', '⚱️', '🏺', 
        '🔮', '📿', '🧿', '💈', '⚗️', '🔭', '🔬', '🕳️', '🩹', '🩺', '💊', '💉', '🧬', 
        '🦠', '🧫', '🧪', '🌡️', '🧹', '🧺', '🧻', '🚽', '🚰', '🚿', '🛁', '🛀', '🧼', 
        '🪒', '🧽', '🧴', '🧷', '🧹', '🧺', '🧻', '🧼', '🧽', '🧯', '🚪', '🛋️', '🪑', 
        '🚽', '🚿', '🧴', '🧷', '🧹', '🧺', '🧻', '🧼', '🧽', '🧯', '🚪'
      ],
      symbols: [
        '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', 
        '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', 
        '🕎', '☯️', '☦️', '🛐', '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', 
        '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳', '🈶', '🈚', '🈺', 
        '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️', '㊗️', '🈴', '🈵', '🈹', '🈲', '🅰️', 
        '🅱️', '🆎', '🆑', '🅾️', '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫', '💯', '💢', 
        '♨️', '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❕', '❓', '❔', '‼️', 
        '⁉️', '🔅', '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️', '🔰', '♻️', '✅', '🈯', '💹', 
        '❇️', '✳️', '❎', '🌐', '💠', 'Ⓜ️', '🌀', '💤', '🏧', '🚾', '♿', '🅿️', '🈳', 
        '🈂️', '🛂', '🛃', '🛄', '🛅', '🚹', '🚺', '🚼', '⚧', '🚻', '🚮', '🎦', '📶', 
        '🈁', '🔣', 'ℹ️', '🔤', '🔡', '🔠', '🆖', '🆗', '🆙', '🆒', '🆕', '🆓', '0️⃣', 
        '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟'
      ]
    };
    
    // האימוג'ים שמוצגים כרגע לפי הקטגוריה הנבחרת
    const currentEmojis = computed(() => {
      return emojisByCategory[currentEmojiCategory.value as keyof typeof emojisByCategory] || emojis;
    });

    // עדכון נתוני המשתמש בצ'אט הנוכחי
    const chatUser = ref<any>({});
    const messages = ref<Message[]>([]);
    
    // פונקציית חזרה לרשימה
    const backToList = (): void => {
      emit("back-to-list");
    };
    
    // פונקציית הפעלת/כיבוי
    const toggleActive = (): void => {
      isActive.value = !isActive.value;
    };
    
    // טעינת משתמשים מהארגון
    const fetchOrgUsers = async () => {
      try {
        // קבלת המשתמש הנוכחי
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        
        if (!session) {
          console.error('No active session');
          return;
        }
        
        // קבלת פרופיל המשתמש כדי לקבל את מזהה הארגון
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', session.user.id)
          .single();
          
        if (profileError || !userProfile) {
          console.error('Error fetching user profile:', profileError);
          return;
        }
        
        // קבלת כל המשתמשים מאותו ארגון
        const { data: users, error: usersError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('org_id', userProfile.org_id);
          
        if (usersError) {
          console.error('Error fetching organization users:', usersError);
          return;
        }
        
        orgUsers.value = users || [];
      } catch (error) {
        console.error('Error fetching organization users:', error);
      }
    };
    
    // בדיקה אם השיחה מוקצית למשתמש
    const checkAssignment = async () => {
      if (!props.selectedChat) return;
      
      try {
        const { data, error } = await supabase
          .from('threads')
          .select('assign_to')
          .eq('id', props.selectedChat.id)
          .single();
          
        if (error) {
          console.error('Error checking thread assignment:', error);
          return;
        }
        
        if (data && data.assign_to) {
          assignedToId.value = data.assign_to;
          await getAssignedUserName();
        } else {
          assignedToId.value = null;
          assignedToName.value = "";
        }
      } catch (error) {
        console.error('Error checking assignment:', error);
      }
    };
    
    // קבלת שם המשתמש שהוקצה
    const getAssignedUserName = async () => {
      if (!assignedToId.value) return;
      
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('display_name')
          .eq('user_id', assignedToId.value)
          .single();
          
        if (error || !data) {
          console.error('Error fetching assigned user name:', error);
          return;
        }
        
        assignedToName.value = data.display_name;
      } catch (error) {
        console.error('Error fetching assigned user name:', error);
      }
    };
    
    // הצגה/הסתרה של הדרופדאון
    const toggleAssignDropdown = () => {
      showAssignDropdown.value = !showAssignDropdown.value;
      
      // אם הדרופדאון מוצג, נטען את המשתמשים מהארגון
      if (showAssignDropdown.value && orgUsers.value.length === 0) {
        fetchOrgUsers();
      }
    };
    
    // הקצאת השיחה למשתמש
    const assignToUser = async (userId: string) => {
      if (!props.selectedChat) return;
      
      try {
        // הקצאת המשתמש לשיחה
        const { error } = await supabase
          .from('threads')
          .update({ assign_to: userId })
          .eq('id', props.selectedChat.id);
          
        if (error) {
          console.error('Error assigning thread:', error);
          return;
        }
        
        // עדכון המידע בממשק
        assignedToId.value = userId;
        await getAssignedUserName();
        
        // קבלת פרטי המשתמש הנוכחי
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        
        if (session) {
          // יצירת התראה חדשה בטבלת notifications
          const { error: notifError } = await supabase
            .from('notifications')
            .insert({
              userid: userId,                          // המשתמש שקיבל את ההקצאה
              timestamp: new Date().toISOString(),     // זמן יצירת ההתראה
              type: 'alert',                           // סוג התראה: alert
              content: `Chat with ${props.selectedChat.name || props.selectedChat.phone || 'a customer'} has been assigned to you.`,
              status: 'unread'                         // סטטוס התראה: לא נקראה
            });
            
          if (notifError) {
            console.error('Error creating notification:', notifError);
          } else {
            console.log('Notification created successfully');
          }
        }
        
        showAssignDropdown.value = false;
      } catch (error) {
        console.error('Error assigning user:', error);
      }
    };
    
    // ביטול הקצאה
    const unassignUser = async () => {
      if (!props.selectedChat) return;
      
      try {
        const { error } = await supabase
          .from('threads')
          .update({ assign_to: null })
          .eq('id', props.selectedChat.id);
          
        if (error) {
          console.error('Error unassigning thread:', error);
          return;
        }
        
        // עדכון המידע בממשק
        assignedToId.value = null;
        assignedToName.value = "";
        showAssignDropdown.value = false;
      } catch (error) {
        console.error('Error unassigning user:', error);
      }
    };
    
    // סגירת הדרופדאון בלחיצה מחוץ אליו
    const closeDropdownOnOutsideClick = (event: MouseEvent) => {
      if (showAssignDropdown.value && !event.target) {
        showAssignDropdown.value = false;
      }
    };
    
    // טיפול בקליקים על המסמך
    const handleDocumentClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      // בודק אם הקליק היה מחוץ לדרופדאון
      if (!target.closest("#dropdownToggle") && isActive.value) {
        isActive.value = false;
      }
      
      // סגירת בורר האימוג'ים אם הקליק היה מחוץ לו
      if (showEmojiPicker.value && !target.closest('.emoji-picker') && !target.closest('.emoji-button')) {
        showEmojiPicker.value = false;
      }
      
      // סגירת דרופדאון ההקצאה אם הקליק היה מחוץ לו
      if (showAssignDropdown.value && !target.closest('.assign-dropdown') && !target.closest('[type="button"]')) {
        showAssignDropdown.value = false;
      }
    };
    
    // פתיחה/סגירה של בורר האימוג'ים
    const toggleEmojiPicker = (e: MouseEvent): void => {
      e.stopPropagation();
      showEmojiPicker.value = !showEmojiPicker.value;
    };
    
    // הוספת אימוג'י לטקסט ההודעה
    const addEmoji = (emoji: string): void => {
      messageText.value += emoji;
    };
    
    // שליחת הודעה
    const sendMessage = async (): Promise<void> => {
      if (!messageText.value.trim() || !props.selectedChat) return;
      
      const userStore = useUserStore();
      const organization_id = userStore.user?.organization_id || props.selectedChat.organization_id;
      
      // המרת מספר טלפון מפורמט בינלאומי לפורמט מקומי (972xxx -> 0xxx)
      const formatPhoneNumberToLocal = (phone: string): string => {
        // אם המספר מתחיל ב-972, החלף ל-0
        if (phone.startsWith('972')) {
          return '0' + phone.substring(3);
        }
        // אחרת החזר את המספר כמו שהוא
        return phone;
      };
      
      // שליחת ההודעה לשרת החיצוני
      const apiPayload = {
        organization_id: organization_id,
        message: messageText.value.trim(),
        phone: formatPhoneNumberToLocal(props.selectedChat.phone)
      };
      
      console.log('Sending message payload:', apiPayload);
      
      try {
        // שימוש ב-axios במקום fetch ושימוש בפרוקסי כדי לעקוף את בעיית ה-CORS
        const response = await axios({
          method: 'post',
          url: '/api/send-message', // שימוש בפרוקסי שמוגדר לשלוח ל-api.clickai.live
          data: apiPayload,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true 
        });
        
        console.log('Message sent successfully to external API', response.data);
        
        // איפוס תיבת הטקסט
        messageText.value = '';
      } catch (err: any) {
        console.error('Failed to send message to API:', err.message || err);
        
        // הצגת ההודעה למשתמש
        if (err.response) {
          // השרת החזיר תשובה עם קוד שגיאה
          console.error('Server responded with error:', {
            status: err.response.status,
            data: err.response.data
          });
          alert(`שגיאה בשליחת ההודעה: ${err.response.status}`);
        } else if (err.request) {
          // הבקשה נשלחה אבל לא התקבלה תשובה
          console.error('No response received from server');
          alert('לא התקבלה תשובה מהשרת. בדוק את החיבור לאינטרנט.');
        } else {
          // שגיאה בהכנת הבקשה
          alert('שגיאה בשליחת ההודעה. נסה שוב מאוחר יותר.');
        }
      }
    };

    // Load messages from the database
    const loadMessages = async () => {
      if (!props.selectedChat?.phone || !props.selectedChat?.organization_id) {
        console.log('Cannot load messages: missing chat data');
        return;
      }
      
      console.log('Loading messages for:', props.selectedChat.phone);
      
      try {
        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .eq('phone', props.selectedChat.phone)
          .eq('organization_id', props.selectedChat.organization_id)
          .order('created_at', { ascending: true });
          
        if (error) {
          console.error('Error loading messages:', error);
          return;
        }
        
        messages.value = data || [];
        console.log(`Loaded ${messages.value.length} messages`);
      } catch (err) {
        console.error('Error loading messages:', err);
      }
    };
    
    // Set up basic lifecycle hooks
    onMounted(() => {
      document.addEventListener("click", handleDocumentClick);
      checkIfMobile();
      window.addEventListener('resize', checkIfMobile);
      
      // טעינת משתמשי הארגון
      fetchOrgUsers();
      
      // הוספת מאזין לסגירת הדרופדאון
      document.addEventListener('click', closeDropdownOnOutsideClick);
    });
    
    onBeforeUnmount(() => {
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener('resize', checkIfMobile);
      
      // Clean up the realtime subscription
      if (messagesSubscription) {
        console.log('Cleaning up realtime subscription');
        supabase.removeChannel(messagesSubscription);
        messagesSubscription = null;
      }
      
      // הסרת מאזינים
      document.removeEventListener('click', closeDropdownOnOutsideClick);
    });

    // Use a realtime subscription to receive new messages in real-time
    let messagesSubscription: any = null;

    const setupMessagesSubscription = () => {
      // Clear any existing subscription
      if (messagesSubscription) {
        supabase.removeChannel(messagesSubscription);
        messagesSubscription = null;
      }
      
      // Don't set up subscription if no chat is selected
      if (!props.selectedChat?.phone || !props.selectedChat?.organization_id) {
        return;
      }
      
      console.log('Setting up realtime subscription for chat messages');
      
      // Subscribe to new messages for this chat
      messagesSubscription = supabase
        .channel('messages-changes')
        .on('postgres_changes', 
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `phone=eq.${props.selectedChat.phone}:organization_id=eq.${props.selectedChat.organization_id}`
          }, 
          (payload) => {
            console.log('Received new message via realtime:', payload);
            
            // Add the new message to our list after ensuring it conforms to Message interface
            if (payload.new) {
              const newMessage: Message = {
                id: payload.new.id || 0,
                text: payload.new.text || '',
                phone: payload.new.phone || '',
                created_at: payload.new.created_at || new Date().toISOString(),
                from_our_customer: payload.new.from_our_customer || false,
                organization_id: payload.new.organization_id || '',
                type: payload.new.type || 'text',
                qtext: payload.new.qtext || null,
                link: payload.new.link || null
              };
              messages.value = [...messages.value, newMessage];
            }
          }
        )
        .subscribe((status) => {
          console.log('Realtime subscription status:', status);
        });
    };

    // Watch for changes to the selected chat
    watch(() => props.selectedChat?.id, (newChatId) => {
      console.log('Selected chat changed to:', newChatId);
      
      // Clear messages when no chat is selected
      if (!newChatId) {
        messages.value = [];
        return;
      }
      
      // Load messages for new chat
      loadMessages();
      
      // Setup realtime subscription for the current chat
      setupMessagesSubscription();
    }, { immediate: true });
    
    // Watch for changes in selectedChat
    watch(() => props.selectedChat, async (newChat) => {
      if (newChat) {
        console.log('Selected chat updated:', newChat);
        await loadMessages();
        chatUser.value = newChat;
        
        // בדיקה אם השיחה מוקצית למישהו
        await checkAssignment();
      }
    }, { immediate: true });

    // Add a helper function to format timestamps
    const formatTime = (timestamp: string | null): string => {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Add a helper function to format message text with proper line breaks and bullet points
    const formatMessageText = (text: string): string => {
      if (!text) return '';
      
      // Escape HTML to prevent XSS
      let formattedText = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
      
      // Convert line breaks to <br>
      formattedText = formattedText.replace(/\n/g, '<br>');
      
      // Handle bullet points (* ) by converting them to proper HTML bullets
      formattedText = formattedText.replace(/\* /g, '• ');
      
      return formattedText;
    };

    // Function to open image in a new tab
    const openImageInNewTab = (imageUrl: string | null): void => {
      if (imageUrl) {
        window.open(imageUrl, '_blank');
      }
    };

    const toggleBotStatus = async (): Promise<void> => {
      if (!props.selectedChat || !props.selectedChat.id) {
        console.log('Cannot toggle bot status: No chat selected or missing thread id');
        return;
      }
      
      const newStatus = !chatUser.value.botstatus;
      
      console.log('Toggling bot status for thread:', {
        id: props.selectedChat.id,
        currentStatus: chatUser.value.botstatus,
        newStatus: newStatus
      });
      
      try {
        const { error } = await supabase
          .from('threads')
          .update({ botstatus: newStatus })
          .eq('id', props.selectedChat.id);
          
        if (error) {
          console.error('Error updating bot status:', error);
          return;
        }
        
        // Emit event to parent instead of mutating prop directly
        emit('update-bot-status', {
          threadId: props.selectedChat.id,
          botstatus: newStatus
        });
        
        console.log('Bot status updated successfully:', newStatus);
      } catch (err) {
        console.error('Failed to update bot status:', err);
      }
    };

    return {
      isActive,
      chatUser,
      toggleActive,
      isMobile,
      backToList,
      directT,
      messageText,
      showEmojiPicker,
      emojis,
      emojiCategories,
      currentEmojiCategory,
      currentEmojis,
      toggleEmojiPicker,
      addEmoji,
      sendMessage,
      formatTime,
      messages,
      toggleBotStatus,
      formatMessageText,
      openImageInNewTab,
      
      // תכונות חדשות להקצאת משתמשים
      showAssignDropdown,
      orgUsers,
      assignedToName,
      toggleAssignDropdown,
      assignToUser,
      unassignUser,
    };
  },
});
</script>

<style scoped>
.calc-height {
  height: calc(100vh - 210px);
  max-height: 100%;
  overflow-y: auto;
}

@media (max-width: 768px) {
  .calc-height {
    height: calc(100vh - 190px);
    max-height: 100%;
  }
}

/* Force the card to stay within its container */
:deep(.trezo-card) {
  max-height: 100vh;
  overflow: hidden;
  box-shadow: 0 10px 50px rgba(0,0,0,0.15) !important;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.2)) !important;
}

:deep(.dark .trezo-card) {
  box-shadow: 0 10px 50px rgba(0,0,0,0.5) !important;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3)) !important;
}

/* Fix button alignment */
button i.material-symbols-outlined {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.chat-form button {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Emoji picker styles */
.emoji-picker {
  box-shadow: 0 5px 25px rgba(0,0,0,0.2);
  border: 1px solid rgba(0,0,0,0.1);
}

.dark .emoji-picker {
  border: 1px solid rgba(255,255,255,0.1);
}

/* Audio player styling */
audio {
  height: 40px;
  border-radius: 20px;
  outline: none;
}

/* Image in chat styling */
.chat-body img {
  border-radius: 8px;
  transition: all 0.2s ease;
}

.chat-body img:hover {
  opacity: 0.9;
  transform: scale(1.01);
}
</style>
